/**
 * Group mode-switch helpers
 * For Media Devoted | Created By Ciro Autiero
 *
 * Pure conversions used when toggling Sliders <-> Blocks inside a group:
 * - sliders -> ast: builds a weighted-sum AST tree from sub-metric rows.
 * - ast -> sliders: detects the "weighted sum of metric*number" pattern; if
 *   present, returns the matching sliders body. Returns null when the AST
 *   cannot be losslessly represented as sliders rows. A "Lowest" row is
 *   `lowest(metric) * number` both ways (02/11/2026).
 */

import type { FormulaBlockNode, FormulaSubMetric } from '@trafficpulse/shared';

const ROUND = (n: number): number => Math.round(n * 100);

export function slidersToAst(metrics: FormulaSubMetric[]): FormulaBlockNode | null {
  const picked = metrics.filter((m) => Boolean(m.metric));
  if (picked.length === 0) return null;
  const factors: FormulaBlockNode[] = effectiveWeights(picked).map(toFactor);
  return factors.reduce((left, right) => ({ kind: 'binary', op: '+', left, right }));
}

/**
 * Mirrors `serializeSliders`: legacy zero-weight rows fall back to an equal
 * split so converting never produces an empty master while the balanced UI
 * displays real shares (FRM-mode-switch-data-loss).
 */
function effectiveWeights(picked: FormulaSubMetric[]): FormulaSubMetric[] {
  const total = picked.reduce((sum, m) => sum + Math.max(0, m.weight), 0);
  if (total <= 0) {
    return picked.map((m) => ({ ...m, weight: 100 / picked.length }));
  }
  return picked
    .filter((m) => m.weight > 0)
    .map((m) => ({ ...m, weight: (m.weight / total) * 100 }));
}

function toFactor(metric: FormulaSubMetric): FormulaBlockNode {
  const node: FormulaBlockNode = { kind: 'metric', metric: metric.metric };
  return {
    kind: 'binary',
    op: '*',
    // A "Lowest" row (02/11/2026) is lowest(metric) in the blocks.
    left: metric.lowest ? { kind: 'call', fn: 'lowest', args: [node] } : node,
    right: { kind: 'num', value: Math.round((metric.weight / 100) * 100) / 100 },
  };
}

export interface AstToSlidersResult {
  metrics: FormulaSubMetric[];
}

export function astToSliders(node: FormulaBlockNode | null): AstToSlidersResult | null {
  if (!node) return { metrics: [] };
  const factors = flattenSum(node);
  const parsed: FormulaSubMetric[] = [];
  for (let i = 0; i < factors.length; i++) {
    const factor = readFactor(factors[i], i);
    if (!factor) return null;
    parsed.push(factor);
  }
  return { metrics: roundToHundred(parsed) };
}

function flattenSum(node: FormulaBlockNode): FormulaBlockNode[] {
  if (node.kind === 'binary' && node.op === '+') {
    return [...flattenSum(node.left), ...flattenSum(node.right)];
  }
  return [node];
}

interface RowMetric {
  metric: string;
  lowest: boolean;
}

/** `metric` or `lowest(metric)` — what a sliders row can hold. */
function rowMetric(node: FormulaBlockNode): RowMetric | null {
  if (node.kind === 'metric') return { metric: node.metric, lowest: false };
  if (node.kind !== 'call' || node.fn !== 'lowest' || node.args.length !== 1) return null;
  const [arg] = node.args;
  return arg.kind === 'metric' ? { metric: arg.metric, lowest: true } : null;
}

function readFactor(node: FormulaBlockNode, index: number): FormulaSubMetric | null {
  const bare = rowMetric(node);
  if (bare) return makeMetric(bare, 1, index);
  if (node.kind !== 'binary' || node.op !== '*') return null;
  const left = rowMetric(node.left);
  if (left && node.right.kind === 'num') return makeMetric(left, node.right.value, index);
  const right = rowMetric(node.right);
  if (right && node.left.kind === 'num') return makeMetric(right, node.left.value, index);
  return null;
}

function makeMetric(row: RowMetric, factor: number, index: number): FormulaSubMetric {
  const metric = { id: `m_${index}`, metric: row.metric, weight: ROUND(factor) };
  return row.lowest ? { ...metric, lowest: true } : metric;
}

function roundToHundred(metrics: FormulaSubMetric[]): FormulaSubMetric[] {
  if (metrics.length === 0) return metrics;
  const total = metrics.reduce((s, m) => s + m.weight, 0);
  if (total === 100) return metrics;
  if (total <= 0) return metrics.map((m, i) => ({ ...m, weight: i === 0 ? 100 : 0 }));
  const scaled = metrics.map((m) => (m.weight / total) * 100);
  const floors = scaled.map((v) => Math.floor(v));
  const remainders = scaled.map((v, i) => ({ i, frac: v - floors[i] }));
  remainders.sort((a, b) => b.frac - a.frac);
  let allocated = floors.reduce((s, v) => s + v, 0);
  const out = metrics.map((m, i) => ({ ...m, weight: floors[i] }));
  let cursor = 0;
  while (allocated < 100 && cursor < remainders.length) {
    out[remainders[cursor++].i].weight++;
    allocated++;
  }
  return out;
}
